import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Translatable } from '@hrlabs/shared-types';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';

const EMPTY: Translatable = { en: '', bg: '' };
const collectionFor = (kind?: string) =>
  kind === 'candidate' ? 'candidateTestimonials' : 'businessPartnerTestimonials';

export function TestimonialEdit() {
  const { kind, id } = useParams();
  const col = collectionFor(kind);
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState<Translatable>({ ...EMPTY });
  const [position, setPosition] = useState<Translatable>({ ...EMPTY });
  const [quote, setQuote] = useState<Translatable>({ ...EMPTY });

  useEffect(() => {
    if (!id) return;
    getRow(col, id).then((r) => {
      if (r) {
        setName((r.name as Translatable) ?? { ...EMPTY });
        setPosition((r.position as Translatable) ?? { ...EMPTY });
        setQuote((r.quote as Translatable) ?? { ...EMPTY });
      }
      setLoading(false);
    });
  }, [col, id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.bg.trim() || !position.bg.trim() || !quote.bg.trim()) {
      notify('danger', 'Bulgarian name, position and quote are required.');
      return;
    }
    setSaving(true);
    try {
      const savedId = await saveRow(col, id, { name, position, quote });
      notify('success', 'Testimonial saved.');
      navigate(`/testimonials/${kind}/${savedId}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 760 }}>
      <h3 className="fw-bold mb-3">{id ? 'Edit testimonial' : 'New testimonial'}</h3>
      <BilingualField label="Name" value={name} onChange={setName} required rows={1} />
      <BilingualField label="Position" value={position} onChange={setPosition} required rows={1} />
      <BilingualField label="Quote" value={quote} onChange={setQuote} required />
      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
